import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { RefreshCw, AlertTriangle, FileDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { usePdfExport } from '@/hooks/usePdfExport';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PendingStock() {
  const [variances, setVariances] = useState([]);
  const [loading, setLoading] = useState(true);
  const { exporting, handleExport } = usePdfExport('/export/pending-stock-pdf', 'Pending_Stock');

  const fetchVariances = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/stock/variance`, {
        
      });
      setVariances(response.data);
    } catch (err) { console.error(err);
      toast.error('Failed to fetch pending stock');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchVariances();
  }, [fetchVariances]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold tracking-tight uppercase" style={{fontFamily: 'Barlow Condensed, sans-serif'}} data-testid="pending-stock-title">PENDING / IN-PROCESS</h1>
          <p className="text-sm text-text-muted mt-2">Parts issued but not yet received (variance tracking)</p>
        </div>
        <div className="flex gap-2">
          <Button
            data-testid="export-pdf-button"
            onClick={() => handleExport()}
            disabled={exporting || variances.length === 0}
            className="bg-green-600 text-white hover:bg-green-700 rounded-sm font-medium"
          >
            <FileDown className="w-4 h-4 mr-2" />
            {exporting ? 'EXPORTING...' : 'EXPORT PDF'}
          </Button>
          <Button
            data-testid="refresh-variance-button"
            onClick={fetchVariances}
            className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm font-medium"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            REFRESH
          </Button>
        </div>
      </div>

      <div className="bg-white border border-border shadow-sm">
        {loading ? (
          <div className="p-12 text-center text-text-muted">Loading variance data...</div>
        ) : variances.length === 0 ? (
          <div className="p-12 text-center">
            <p className="text-text-muted">No pending quantities - all issued parts have been received!</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full caption-bottom text-sm">
              <thead>
                <tr className="border-b">
                  <th className="h-10 px-4 text-left align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">
                    <AlertTriangle className="w-4 h-4 inline mr-2 text-warning" />
                    Part ID
                  </th>
                  <th className="h-10 px-4 text-left align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Part Name</th>
                  <th className="h-10 px-4 text-left align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Stage</th>
                  <th className="h-10 px-4 text-right align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Issued</th>
                  <th className="h-10 px-4 text-right align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Received</th>
                  <th className="h-10 px-4 text-right align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Pending</th>
                </tr>
              </thead>
              <tbody>
                {variances.map((item, idx) => (
                  <tr key={idx} className="border-b transition-colors hover:bg-muted/50" data-testid={`variance-row-${idx}`}>
                    <td className="p-4 align-middle font-mono text-xs">{item.part_id}</td>
                    <td className="p-4 align-middle">{item.part_name || '-'}</td>
                    <td className="p-4 align-middle">
                      <span className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold border-transparent bg-amber-50 text-amber-700">
                        {item.stage}
                      </span>
                    </td>
                    <td className="p-4 align-middle text-right font-mono text-xs">{item.issued}</td>
                    <td className="p-4 align-middle text-right font-mono text-xs">{item.received}</td>
                    <td className="p-4 align-middle text-right font-mono text-xs">
                      <span className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold border-transparent bg-red-50 text-red-700">
                        {item.pending}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="bg-amber-50 border border-amber-200 p-4 rounded-sm">
        <h3 className="text-sm font-semibold text-amber-900 mb-2 uppercase tracking-wide">About Pending Stock</h3>
        <p className="text-xs text-amber-800">
          This shows parts that have been issued for processing but not yet received back. 
          Pending quantities indicate work-in-progress at internal stages (not vendor operations - see Vendor WIP for that).
        </p>
      </div>
    </div>
  );
}
